import React, { useEffect, useState } from 'react';
import { Logger } from '@saviynt/common';
import {
  ButtonIcon,
  Grid,
  Icon,
  Loader,
  Typography,
} from '@saviynt/design-system';

import useGetDashboardDetails from '../../../../hooks/use-getdashboarddetails';
import { type UserCount, type UserType } from '../../../../services/types';
import { useInvertedLocalize } from '../../../../utils';
import { COLORS } from '../../../../utils/constants';
import { ExternalBanner, UsersList } from '../../../Common';
import ExternalBlog from '../../../ExternalBog/ExternalBlog';
import JOB_STATUS from '../../constants';
import Analytics from '../Analytics/Analytics';
import Insights from '../Insights/Insights';
import ExternalLanding from '../LandingPage/ExternalLandingPage';
import Recommendations from '../Recommendations/Recommendations';

import invertedMessages from './messages';

import './ExternalIdentities.css';

interface ExternalIdentitiesProps {
  isJobTriggerSuccess: boolean;
}

function ExternalIdentities({ isJobTriggerSuccess }: ExternalIdentitiesProps) {
  const [isJobInProgress, setIsJobInProgress] = useState<boolean>(false);
  const [isJobCompleted, setIsJobCompleted] = useState<boolean>(false);
  const [isJobNotStarted, setIsJobNotStarted] = useState<boolean>(false);
  const [showMoreInsights, setShowMoreInsights] = useState<boolean>(false);
  const [externalIdentities, setExternalIdentities] = useState<UserCount>();
  const l = useInvertedLocalize(invertedMessages);
  const { Item } = Grid;
  const {
    data,
    isLoading: isLoadingDashboardData,
    refetch: refetchDashboardData,
    isFetching: isFetchingDashboardData,
  } = useGetDashboardDetails();

  const isLoading = isLoadingDashboardData || isFetchingDashboardData;

  useEffect(() => {
    async function fetchData() {
      if (isJobTriggerSuccess) {
        await refetchDashboardData();
      }
    }

    fetchData().catch(Logger.error);
  }, [isJobTriggerSuccess, refetchDashboardData]);

  useEffect(() => {
    if (data?.jobStatus) {
      setIsJobInProgress(data.jobStatus.status === JOB_STATUS.IN_PROGRESS);
      setIsJobCompleted(data.jobStatus.status === JOB_STATUS.DONE);
      setIsJobNotStarted(data.jobStatus.status === JOB_STATUS.NOT_STARTED);
    }

    if (data?.externalUsersBreakdown) {
      const usersWithColors = data.externalUsersBreakdown.data.map(
        (identity: UserType, index: number) => ({
          ...identity,
          color: COLORS[index],
        })
      );

      setExternalIdentities({
        total: data.externalUsersBreakdown.total,
        data: usersWithColors,
      });
    }
  }, [data]);

  return (
    <div className='Ext-ExternalIdentities'>
      {isLoading && (
        <div className='Ext-ExternalIdentities-loader'>
          <Loader kind='loop' format='main' color='brand' size='large' />
        </div>
      )}
      {isJobNotStarted && !isLoading && <ExternalLanding />}
      {!isJobNotStarted && !isLoading && (
        <div className='Ext-ExternalIdentities-content'>
          {isJobCompleted && externalIdentities && (
            <Analytics data={externalIdentities} />
          )}
          {isJobCompleted && !externalIdentities && (
            <div className='Ext-ExternalIdentities-content-noIdentities'>
              <Typography kind='h3' color='--color-foreground-neutral-medium'>
                {l(
                  'No users have been classified as external. Please review your external classification rules to ensure they accurately identify external users.'
                )}
              </Typography>
            </div>
          )}
          {isJobInProgress && (
            <ExternalBanner
              heading={l('Classification In Progress')}
              subHeading={l(
                `We’re processing and tagging all your external employees. This may take a while, and we’ll notify you once the classification is done. An external insight dashboard will be created momentarily. While you’re waiting, you can get to know more about how External would help manage your organization’s employees!`
              )}
              showButton={false}
              showClose={false}
              illustration='EvaluatingRequest'
            />
          )}

          {showMoreInsights && (
            <>
              {isJobCompleted && (
                <Grid
                  className='Ext-ExternalIdentities-content-insightsBreakdown'
                  spacing={2}>
                  {data?.insights && (
                    <Item
                      className='Ext-ExternalIdentities-content-insightsBreakdown-item'
                      desktop={9}
                      tablet={12}>
                      <Insights data={data.insights} />
                    </Item>
                  )}
                  {data?.recommendations && (
                    <Item
                      className='Ext-ExternalIdentities-content-insightsBreakdown-item'
                      desktop={3}
                      tablet={12}>
                      <Recommendations data={data.recommendations} />
                    </Item>
                  )}
                </Grid>
              )}
              {isJobInProgress && data?.blogs && (
                <div>
                  <div className='Ext-ExternalIdentities-content-blogs'>
                    <Typography
                      kind='h2'
                      className='Ext-ExternalIdentities-content-blogs-heading'>
                      {l('Learn more about External!')}
                    </Typography>
                    <ExternalBlog data={data.blogs} />
                  </div>
                </div>
              )}
            </>
          )}
          {!isLoading && (
            <div className='Ext-ExternalIdentities-content-btn'>
              <ButtonIcon
                size='large'
                kind='outlinedSecondary'
                icon={
                  !showMoreInsights ? (
                    <Icon kind='ChevronDownSmall' size='medium' />
                  ) : (
                    <Icon kind='ChevronUpSmall' size='medium' />
                  )
                }
                onClick={() => {
                  setShowMoreInsights((prev) => !prev);
                }}
                ariaLabel={showMoreInsights ? 'Navigate down' : 'Navigate up'}
              />
            </div>
          )}
        </div>
      )}
      <div className='Ext-ExternalIdentities-users'>
        <UsersList
          jobInProgress={isJobInProgress}
          userType='EXTERNAL'
          searchPlaceholder={l('Search external identities')}
          isJobNotStarted={isJobNotStarted}
          isDashboardDataLoading={isLoading}
        />
      </div>
    </div>
  );
}

export default ExternalIdentities;
